import { computed, action, observable } from "mobx";
import Autobind from "autobind-decorator";
import RootStore from "./rootStore";

import Cache from "core/cache/index";
/// Screen Control Modules
import { toggleFullScreen } from "core/screen/screenManager.js";
import Config from "config";
import { ToolEventDispatcher } from "component/molecules/viewport/core/eventDispatcher";

import {
  setToolActive,
  setToolPassive,
} from "component/molecules/viewport/toolStates/setToolMode.js";
import { getters } from "component/molecules/viewport/toolStates/store.js";

import {
  getEnabledElementById,
  getEnabledElements,
} from "component/toolManager/enabledElements.js";
import getActiveToolsForElement from "component/molecules/viewport/toolStates/getActiveToolsForElement.js";
import {
  setToolEnabledForElement,
  setToolDisabledForElement,
  isActiveToolModeForElement,
} from "component/molecules/viewport/toolStates/setToolMode";
import makeKey from "core/cache/KeyGen";
import CustomEvent from "core/customEvent/index";
import _ from "lodash";
/**
 * @class WorklistStore
 * @description 요청에 따른 Study 목록을 반환
 */
@Autobind
class ToolbarStore {
  _rootStore: RootStore; /// rootStore 객체. 다른 Store를 연계할 때 사용
  @observable _selectedViewportTypeTool: number = -1;
  @observable _selectedMeasureTypeTool: string = "length";
  @observable _isImageFusion: boolean = false;
  @observable _isShowDCMDescription: boolean = true;
  @observable _isFullscreen: boolean = false;
  @observable _isBatchViewMode: number = Config.control
    .AllocateAllFramesOnLayout
    ? 1
    : -1;
  @observable _isMPRactive: boolean = false;

  //_isBatchViewMode: number = -1;

  constructor(rootStore: RootStore) {
    this._rootStore = rootStore;
  }
  setIsMPRActive(active: boolean) {
    this._isMPRactive = active;
  }
  getIsMPRActive() {
    return this._isMPRactive;
  }
  isOpenSeriesList() {
    return this._rootStore.seriesListStore.getIsOpen();
  }
  toggleOpenSeriesList(open: boolean) {
    this._rootStore.seriesListStore.toggleIsOpen();
  }

  isFullscreen() {
    return this._isFullscreen;
  }

  toggleFullscreenState() {
    this._isFullscreen = !this._isFullscreen;
  }

  toggleFullscreen() {
    this._isFullscreen = !this._isFullscreen;
    toggleFullScreen();
  }

  isShowDCMDescription() {
    return this._isShowDCMDescription;
  }
  toggleShowDCMDescription() {
    this._isShowDCMDescription = !this._isShowDCMDescription;
  }

  isActiveMeasureTypeTool(toolName: string) {
    if (this._selectedMeasureTypeTool === toolName) {
      return true;
    }
    return false;
  }
  getActiveMeasureTypeTool() {
    return this._selectedMeasureTypeTool;
  }
  reactivateMeasureTypeTool() {
    setToolActive(
      this._selectedMeasureTypeTool,
      { mouseButtonMask: 1 },
      undefined as any
    );
    let toolEventDispatcher = new ToolEventDispatcher();
    toolEventDispatcher.setActivateTool("none");
  }
  @action
  activateMeasureTypeTool(toolName: string) {
    setToolActive(toolName, { mouseButtonMask: 1 }, undefined as any);
    let toolEventDispatcher = new ToolEventDispatcher();
    toolEventDispatcher.setActivateTool("none");
    //store.SelectedMeasure.setSelectedMeasure(toolName);
    this._rootStore.measureToolStore.setActiveToolName(toolName);
    this._selectedMeasureTypeTool = toolName;
    this._selectedViewportTypeTool = 4;
  }
  activateViewportToolName(toolName: string) {
    //store.SelectedMeasure.setSelectedMeasure("");
    this._rootStore.measureToolStore.setActiveToolName(toolName);
    this.activateTool(toolName);
  }
  /// 임시로, 구현. 추후 다른 함수에 흡수
  activateTool(toolName: string) {
    let toolEventDispatcher = new ToolEventDispatcher();
    toolEventDispatcher.setActivateTool(toolName);

    let allEnabledElements = getEnabledElements();
    for (let i = 0; i < allEnabledElements.length; i++) {
      let tools = getActiveToolsForElement(
        allEnabledElements[i].element,
        getters.mouseTools(),
        undefined as any
      );
      tools = tools.filter((tool: any) => tool.options.isMouseActive);
      if (tools.length > 0) {
        setToolPassive(tools[0].name, undefined);
      }
    }
  }

  activateViewportToolType(toolType: number) {
    if (toolType === -1) {
      this.activateViewportToolName("BrowseSeries");
    } else if (toolType === 1) {
      this.activateViewportToolName("Pan");
    } else if (toolType === 2) {
      this.activateViewportToolName("Zoom");
    } else if (toolType === 3) {
      this.activateViewportToolName("Contrast");
    }
  }

  setViewportToolType(toolType: number) {
    this._selectedViewportTypeTool = toolType;

    if (this._selectedViewportTypeTool === 4) {
      this.reactivateMeasureTypeTool();
    } else {
      this.activateViewportToolType(this._selectedViewportTypeTool);
    }
  }
  getImageFusionButtonState() {
    if (this._isImageFusion === true) {
      return 1;
    }
    return 0;
  }
  getImageFusion() {
    return this._isImageFusion;
  }

  @action
  checkImageFusionState() {
    const activeDCMViewIdxes = this._rootStore.gridLayoutStore.getActiveDCMViews();
    const enabledElement = getEnabledElementById(activeDCMViewIdxes[0]);
    const isActive = isActiveToolModeForElement(
      enabledElement.element,
      "ImageFusion"
    );
    this._isImageFusion = isActive;
  }
  findImageFusionTarget() {
    const activeDCMViewIdxes = this._rootStore.gridLayoutStore.getActiveDCMViews();
    if (activeDCMViewIdxes.length === 0) {
      return false;
    }
    const sourceEnabledElement = getEnabledElementById(activeDCMViewIdxes[0]);

    if (!!_.get(sourceEnabledElement, "image", null) === false) {
      return false;
    }

    const data = makeKey(
      sourceEnabledElement.image.seriesInstanceUID,
      sourceEnabledElement.image.modality
    );
    let metadataCache = new Cache.Metadata();
    const allEnabledElements = getEnabledElements();
    const sourceStudyInstanceUID = metadataCache.getStudyInstanceUID(data);
    const sourceModality = sourceEnabledElement.image.modality;
    const targetEnabledElement = allEnabledElements.filter((enabledElement) => {
      if (!!enabledElement.image === false) {
        return false;
      }
      const testKey = makeKey(
        enabledElement.image.seriesInstanceUID,
        enabledElement.image.modality
      );
      const testStudyInstanceUID = metadataCache.getStudyInstanceUID(testKey);
      if (sourceStudyInstanceUID !== testStudyInstanceUID) {
        return false;
      }
      if (
        (sourceModality.toLowerCase() === "pt" &&
          enabledElement.image.modality.toLowerCase() === "ct") ||
        (sourceModality.toLowerCase() === "ct" &&
          enabledElement.image.modality.toLowerCase() === "pt")
      ) {
        return true;
      }
      return false;
    });
    if (targetEnabledElement.length === 0) {
      return false;
    }
    return true;
  }
  handleFusionTargetUpdate(event: any) {
    const sourceEnabledElement = event.detail;
    const data = makeKey(
      sourceEnabledElement.image.seriesInstanceUID,
      sourceEnabledElement.image.modality
    );
    let metadataCache = new Cache.Metadata();
    const allEnabledElements = getEnabledElements();
    const sourceStudyInstanceUID = metadataCache.getStudyInstanceUID(data);
    const sourceModality = sourceEnabledElement.image.modality;
    const targetEnabledElement = allEnabledElements.filter((enabledElement) => {
      if (!!enabledElement.image === false) {
        return false;
      }
      const testKey = makeKey(
        enabledElement.image.seriesInstanceUID,
        enabledElement.image.modality
      );
      const testStudyInstanceUID = metadataCache.getStudyInstanceUID(testKey);
      if (sourceStudyInstanceUID !== testStudyInstanceUID) {
        return false;
      }
      if (
        (sourceModality.toLowerCase() === "pt" &&
          enabledElement.image.modality.toLowerCase() === "ct") ||
        (sourceModality.toLowerCase() === "ct" &&
          enabledElement.image.modality.toLowerCase() === "pt")
      ) {
        return true;
      }
      return false;
    });
    if (targetEnabledElement.length === 0) {
      return;
    }
    const targetElementStub = targetEnabledElement[0];

    CustomEvent.triggerEvent(
      targetElementStub.element,
      "forceUpdate",
      targetElementStub
    );
  }
  disableImageFusionForAll() {
    this._isImageFusion = false;
    const enabledElements = getEnabledElements();
    enabledElements.map((enabledElement) => {
      const domElement = enabledElement.element;
      if (!!domElement === true) {
        setToolDisabledForElement(domElement, "ImageFusion", {
          synchronizationContext:
            Config.rootStore.synchronizationStore._updateImageSync,
        });
      }
    });
  }
  disableImageFusion() {
    this._isImageFusion = false;
    const activeDCMViewIdxes = this._rootStore.gridLayoutStore.getActiveDCMViews();
    if (activeDCMViewIdxes.length === 0) {
      return;
    }
    const enabledElement = getEnabledElementById(activeDCMViewIdxes[0]);
    const domElement = enabledElement.element;
    setToolDisabledForElement(enabledElement.element, "ImageFusion", {
      synchronizationContext:
        Config.rootStore.synchronizationStore._updateImageSync,
    });
  }
  @action
  toggleImageFusion() {
    const isImageFusionTargetExists = this.findImageFusionTarget();
    if (isImageFusionTargetExists === false) {
      return;
    }
    this._isImageFusion = !this._isImageFusion;
    const activeDCMViewIdxes = this._rootStore.gridLayoutStore.getActiveDCMViews();
    if (activeDCMViewIdxes.length === 0) {
      return;
    }
    const enabledElement = getEnabledElementById(activeDCMViewIdxes[0]);
    const domElement = enabledElement.element;
    if (this._isImageFusion === true) {
      setToolEnabledForElement(enabledElement.element, "ImageFusion", {
        synchronizationContext:
          Config.rootStore.synchronizationStore._updateImageSync,
      });
    } else {
      setToolDisabledForElement(enabledElement.element, "ImageFusion", {
        synchronizationContext:
          Config.rootStore.synchronizationStore._updateImageSync,
      });
    }
  }
  @action
  toogleViewportToolType(toolType: number) {
    if (this._selectedViewportTypeTool === toolType) {
      this._selectedViewportTypeTool = -1;
    } else {
      this._selectedViewportTypeTool = toolType;
    }
    this.activateViewportToolType(this._selectedViewportTypeTool);
    if (this._selectedViewportTypeTool === 4) {
      this.reactivateMeasureTypeTool();
    }
  }

  setSelectedToolType(toolType: number) {
    this._selectedViewportTypeTool = toolType;
  }
  getSelectedToolType() {
    return this._selectedViewportTypeTool;
  }

  isBatchViewMode() {
    if (this._isBatchViewMode === -1) {
      return false;
    }
    return true;
  }

  getBatchViewMode() {
    return this._isBatchViewMode;
  }

  toogleBatchViewMode(mode: number) {
    if (this._isBatchViewMode === mode) {
      this._isBatchViewMode = -1;
      Config.control.AllocateAllFramesOnLayout = false;
      this._rootStore.gridLayoutStore.changeLayout(1, 1);
      this._rootStore.gridLayoutStore.setLayoutTotalPages(1);
    } else {
      this._isBatchViewMode = mode;
      Config.control.AllocateAllFramesOnLayout = true;
      //this._rootStore.gridLayoutStore.changeLayout(3, 4);
      this._rootStore.gridLayoutStore.changeLayout(2, 3);
    }
  }

  setBatchViewMode(isBatchMode: boolean) {
    if (isBatchMode === true) {
      this._isBatchViewMode = 1;
      Config.control.AllocateAllFramesOnLayout = true;
      //this._rootStore.gridLayoutStore.changeLayout(3, 4);
      this._rootStore.gridLayoutStore.changeLayout(2, 3);
    } else {
      this._isBatchViewMode = -1;
      Config.control.AllocateAllFramesOnLayout = false;
      this._rootStore.gridLayoutStore.changeLayout(1, 1);
      this._rootStore.gridLayoutStore.setLayoutTotalPages(1);
    }
  }
}

export default ToolbarStore;
